import React from "react";
import { connect } from "react-redux";
import { createSelector } from "reselect";
import { motion, AnimatePresence } from "framer-motion";

// Material-UI components
import Typography from "@material-ui/core/Typography";
import KeyboardArrowRight from "@material-ui/icons/KeyboardArrowRight";
import KeyboardArrowLeft from "@material-ui/icons/KeyboardArrowLeft";
import Grid from "@material-ui/core/Grid";
import IconButton from "@material-ui/core/IconButton";
import EditIcon from "@material-ui/icons/Edit";
import Collapse from "@material-ui/core/Collapse";
import Card from "@material-ui/core/Card";
import Radio from "@material-ui/core/Radio";
import RadioGroup from "@material-ui/core/RadioGroup";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import Slider from "@material-ui/core/Slider";
import Menu from "@material-ui/core/Menu";
import ListItemSecondaryAction from "@material-ui/core/ListItemSecondaryAction";
import ListItemText from "@material-ui/core/ListItemText";
import Button from "@material-ui/core/Button";
import Switch from "@material-ui/core/Switch";
import ListItem from "@material-ui/core/ListItem";

// HOC for applying styles to class component
import { withStyles } from "@material-ui/core/styles";
import classNames from "classnames";
// Actions
import {
  changeLocationMapCoordinates,
  setHeatmapLimit,
  setHeatmapType,
  toggleHeatmap,
  setCurrentMapId,
} from "../../actions";

// import other files in the application
import ColorScale from "../Shared/ColorScale";
import MapRegion from "./MapRegion";
import { getSymmetricCoordinates } from "../../constants/utilityFunctions";
import { customTheme } from "../../constants/customTheme";
import {
  tempLocationId,
  tempLocationSymmetricalId,
  customFaceMapId,
  faceMapId,
  heatmapEmodeType,
  heatmapTmodeType,
} from "../../constants/constants";
const styles = {
  root: {
    flexDirection: "column",
    height: "100%",
    display: "flex",
  },
  mapContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flex: "1 0 0",
    maxHeight: "100%",
    padding: "10px 20px 10px 20px",
    position: "relative",
    overflow: "hidden",
  },
  image: {
    objectFit: "contain",
    maxHeight: "100%",
    maxWidth: "100%",
    zIndex: 1,
  },
  locationsContainer: {
    position: "absolute",
  },
  colorScaleWrapper: {
    position: "absolute",
    height: "40%",
    right: "20px",
    transition: "right 0.3s ease",
  },
  bodyMapTitleContainer: {
    height: "48px",
    display: "flex",
    alignItems: "center",
  },
  bodyMapTitle: {
    position: "absolute",
    left: "50%",
    transform: "translateX(-50%)",
    height: "48px",
    display: "flex",
    alignItems: "center",
    // flex: 0 1 auto;
  },
  editButton: {
    padding: "0px 23px 0px 0px",
    marginLeft: "auto",
  },
  listItem: {
    paddingTop: "0px",
    paddingBottom: "0px",
    width: "250px",
    minHeight: "40px",
  },
  heatmapTypeItem: {
    paddingLeft: "20px",
    "& .MuiFormControlLabel-label": {
      fontSize: "0.875rem",
    },
  },
  collapse: {
    boxShadow: "unset",
  },
  dialogRoot: (props) => customTheme(props.theme).dialog.root,
  dialogTitle: customTheme().dialog.title,
  dialogContentContainer: customTheme().dialog.contentContainer,
  dialogButtonContainer: customTheme().dialog.buttonContainer,
  dialogButtonPrimary: (props) => customTheme(props.theme).buttonPrimary,
  dialogButtonOutlined: (props) => customTheme(props.theme).buttonOutlined,
  sliderDiv: {
    width: "100%",
    marginBottom: "15px",
  },
  sliderContainer: {
    margin: "25px 20px 40px 20px",
    width: "90%",
  },
};

// Needs to be a class component to have access to lifecycle methods and refs
class BodyMap extends React.PureComponent {
  constructor(props) {
    super(props);
    this.imageRef = React.createRef(); // In React you can create a reference to an element so that the reference variable can be reused by functions
    this.locationsContainer = React.createRef();

    this.state = {
      imgHeight: 200, // height of bodyMap image. 200 is an default estimation.
      imgWidth: 300, // width of bodyMap image. 300 is an default estimation.
      dragId: null, // locationId of the location that is being repositioned
      imgHover: false, // change region styling
      anchorEl: null,
      heatmapTypeCollapse: this.props.heatmap.status,
      heatmapLimits: {
        swsLimit: [
          this.props.heatmap.swsLimit[0],
          this.props.heatmap.swsLimit[1],
        ],
        thicknessLimit: [
          this.props.heatmap.thicknessLimit[0],
          this.props.heatmap.thicknessLimit[1],
        ],
        heatmapLimitDialog: false,
      },
    };
  }

  measureImage = () => {
    // Measure the size of the image and update the local state (this.state)
    if (this.props.currentLocationView === "BODY_MAP" || this.props.leftView) {
      const height = this.imageRef.current
        ? this.imageRef.current.offsetHeight
        : 200;
      const width = this.imageRef.current
        ? this.imageRef.current.offsetWidth
        : 300;
      // only update state if theres a change
      if (height !== this.state.imgHeight || width !== this.state.imgWidth) {
        this.setState({
          ...this.state,
          imgHeight: height,
          imgWidth: width,
        });
      }
    }
  };

  componentDidMount() {
    // console.log("mounted!");
    // add event listener for window resize event
    this.measureImage();
    window.addEventListener("resize", this.measureImage);
    setTimeout(() => {
      this.measureImage();
    }, 100);
    setTimeout(() => {
      this.measureImage();
    }, 200);
    setTimeout(() => {
      this.measureImage();
    }, 400);
    setTimeout(() => {
      this.measureImage();
    }, 600);
    setTimeout(() => {
      this.measureImage();
    }, 900);
  }

  componentDidUpdate(prevProps) {
    if (this.state.imgHeight === 0) {
      this.measureImage();
    }
    if (
      prevProps.acquireBar !== this.props.acquireBar ||
      prevProps.leftView !== this.props.leftView ||
      prevProps.currentMapId !== this.props.currentMapId ||
      prevProps.maps[prevProps.currentMapId].image !==
        this.props.maps[this.props.currentMapId].image
    ) {
      // console.log("updated!!");
      // wait till 0.3s acquire bar animation is complete to measure
      setTimeout(() => {
        this.measureImage();
      }, 300);
      setTimeout(() => {
        this.measureImage();
      }, 600);
      setTimeout(() => {
        this.measureImage();
      }, 800);
    }
  }

  componentWillUnmount() {
    // remove event listener when component unmounts
    window.removeEventListener("resize", this.measureImage);
  }

  dragStart = (event, locId, dragType) => {
    // Initiate dragging location region
    // Need to save the current position of the cursor to calculate theabsolute movement from the original position
    // console.log("DRAG START");
    if (dragType === "TOUCH") {
      this.setState({
        ...this.state,
        dragId: locId,
        dragXOffset: event.changedTouches[0].clientX,
        dragYOffset: event.changedTouches[0].clientY,
      });
    } else if (dragType === "CURSOR") {
      this.setState({
        ...this.state,
        dragId: locId,
        dragXOffset: event.clientX,
        dragYOffset: event.clientY,
      });
    }
  };

  onDragOver = (evt, dragType) => {
    // Dragging region over the image. As drag event occurs, update the coordinates of the region
    // evt.preventDefault();
    // evt.persist();
    if (this.state.dragId !== null) {
      // console.log("dragging", evt.clientX, evt.clientY);
      // Get position of the cursor
      let targetChildren;
      let clientX;
      let clientY;
      if (dragType === "CURSOR") {
        targetChildren = evt.target.children[this.state.dragId];
        clientX = evt.clientX;
        clientY = evt.clientY;
      } else if (dragType === "TOUCH") {
        targetChildren = evt.target;
        clientX = evt.changedTouches[0].clientX;
        clientY = evt.changedTouches[0].clientY;
      }

      // Calculate how far the region has been dragged in percentage
      var dragX =
        ((clientX - this.state.dragXOffset) * 100) / this.state.imgWidth;
      var dragY =
        ((clientY - this.state.dragYOffset) * 100) / this.state.imgHeight;

      // Add the drag to the original coordinate and update the region
      let coords = this.props.protocolLocations[this.state.dragId].maps[
        this.props.currentMapId
      ];
      if (coords.length === 3) {
        if (targetChildren) {
          targetChildren.setAttributeNS(
            null,
            "cx",
            ((coords[1] + dragX) * (this.state.imgWidth / 100)).toString()
          );
          targetChildren.setAttributeNS(
            null,
            "cy",
            ((coords[2] + dragY) * (this.state.imgHeight / 100)).toString()
          );

          // Update the symmetrical location when adding new loc
          if (
            this.state.dragId === tempLocationId &&
            document.getElementById(tempLocationSymmetricalId)
          ) {
            let symmetricCoords = getSymmetricCoordinates([
              coords[0],
              coords[1] + dragX,
              coords[2] + dragY,
            ]);
            document
              .getElementById(tempLocationSymmetricalId)
              .setAttributeNS(
                null,
                "cx",
                (symmetricCoords[1] * (this.state.imgWidth / 100)).toString()
              );
            document
              .getElementById(tempLocationSymmetricalId)
              .setAttributeNS(
                null,
                "cy",
                (symmetricCoords[2] * (this.state.imgHeight / 100)).toString()
              );
          }
        }
      } else {
        let scaled = [];
        for (var i = 0; i < coords.length; i++) {
          if (i % 2 === 0) {
            scaled.push((coords[i] + dragX) * (this.state.imgWidth / 100));
          } else {
            scaled.push((coords[i] + dragY) * (this.state.imgHeight / 100));
          }
        }
        if (targetChildren) {
          targetChildren.setAttributeNS(null, "points", scaled.toString());
        }
      }
    }
  };

  dragEnd = (evt, dragType) => {
    // REgion is released. Update teh redux coordinate of the region with the new set of coordinates
    if (this.state.dragId !== null) {
      // console.log("DRAG END.");
      let clientX;
      let clientY;
      if (dragType === "CURSOR") {
        clientX = evt.clientX;
        clientY = evt.clientY;
      } else if (dragType === "TOUCH") {
        clientX = evt.changedTouches[0].clientX;
        clientY = evt.changedTouches[0].clientY;
      }

      var dragX =
        ((clientX - this.state.dragXOffset) * 100) / this.state.imgWidth;
      var dragY =
        ((clientY - this.state.dragYOffset) * 100) / this.state.imgHeight;

      let coords = this.props.protocolLocations[this.state.dragId].maps[
        this.props.currentMapId
      ];
      let scaled = [];
      if (coords.length === 3) {
        scaled = [coords[0], coords[1] + dragX, coords[2] + dragY];
      } else {
        for (var i = 0; i < coords.length; i++) {
          if (i % 2 === 0) {
            scaled.push(coords[i] + dragX);
          } else {
            scaled.push(coords[i] + dragY);
          }
        }
      }
      this.props.dispatch(
        changeLocationMapCoordinates(
          this.state.dragId,
          this.props.currentMapId,
          scaled
        )
      );
      this.setState({
        ...this.state,
        dragId: null,
        dragXOffset: 0,
        dragYOffset: 0,
      });
    }
  };

  rotateBodyMap = (dir) => {
    if (dir === "NEXT") {
      if (
        this.props.mapIds.indexOf(this.props.currentMapId) ===
        this.props.mapIds.length - 1
      ) {
        this.props.dispatch(setCurrentMapId(this.props.mapIds[0]));
      } else {
        this.props.dispatch(
          setCurrentMapId(
            this.props.mapIds[
              this.props.mapIds.indexOf(this.props.currentMapId) + 1
            ]
          )
        );
      }
    } else if (dir === "BACK") {
      if (this.props.mapIds.indexOf(this.props.currentMapId) === 0) {
        this.props.dispatch(
          setCurrentMapId(this.props.mapIds[this.props.mapIds.length - 1])
        );
      } else {
        this.props.dispatch(
          setCurrentMapId(
            this.props.mapIds[
              this.props.mapIds.indexOf(this.props.currentMapId) - 1
            ]
          )
        );
      }
    }
  };

  handleChangeHeatmapLimit = () => {
    this.props.dispatch(
      setHeatmapLimit({
        swsLimit: this.state.heatmapLimits.swsLimit,
        thicknessLimit: this.state.heatmapLimits.thicknessLimit,
      })
    );
    this.setState({
      ...this.state,
      heatmapLimits: { ...this.state.heatmapLimits, heatmapLimitDialog: false },
    });
  };

  render() {
    const { classes } = this.props;
    const maps = this.props.maps;

    return (
      <Grid item className={classes.root}>
        {/* MAP NAME/TITLE */}
        <div className={classes.bodyMapTitleContainer}>
          <div className={classes.bodyMapTitle}>
            {this.props.mapIds.length > 1 && (
              <IconButton
                disableRipple
                onClick={() => this.rotateBodyMap("BACK")}
              >
                <KeyboardArrowLeft />
              </IconButton>
            )}
            <Typography>{maps[this.props.currentMapId].name}</Typography>
            {this.props.mapIds.length > 1 && (
              <IconButton
                disableRipple
                onClick={() => this.rotateBodyMap("NEXT")}
              >
                <KeyboardArrowRight />
              </IconButton>
            )}
          </div>
          {this.props.leftView && (
            <IconButton
              disableRipple
              className={classes.editButton}
              onClick={(event) =>
                this.setState({
                  ...this.state,
                  anchorEl: event.currentTarget,
                })
              }
              disabled={this.props.disableEditMenu}
            >
              <EditIcon />
            </IconButton>
          )}
          {this.props.leftView && (
            <Menu
              anchorEl={this.state.anchorEl}
              keepMounted
              open={Boolean(this.state.anchorEl)}
              onClose={() => this.setState({ ...this.state, anchorEl: null })}
            >
              <ListItem ContainerComponent="div" className={classes.listItem}>
                <ListItemText primary="Heat Map" />
                <ListItemSecondaryAction>
                  <Switch
                    disabled={this.props.editMapMode}
                    onChange={() => {
                      if (this.props.heatmap.status) {
                        this.setState({
                          ...this.state,
                          heatmapTypeCollapse: false,
                        });
                      } else {
                        this.setState({
                          ...this.state,
                          heatmapTypeCollapse: true,
                        });
                      }
                      this.props.dispatch(toggleHeatmap());
                    }}
                    checked={this.props.heatmap.status}
                    color="primary"
                  />
                </ListItemSecondaryAction>
              </ListItem>

              <Collapse
                in={this.state.heatmapTypeCollapse}
                timeout="auto"
                unmountOnExit
              >
                <Card className={classes.collapse}>
                  <RadioGroup
                    value={this.props.heatmap.type}
                    onChange={(event) =>
                      this.props.dispatch(setHeatmapType(event.target.value))
                    }
                  >
                    <FormControlLabel
                      value={heatmapEmodeType}
                      className={classes.heatmapTypeItem}
                      control={<Radio size="small" color="primary" />}
                      label="E-Mode"
                    />
                    <FormControlLabel
                      value={heatmapTmodeType}
                      className={classes.heatmapTypeItem}
                      control={<Radio size="small" color="primary" />}
                      label="T-Mode"
                    />
                  </RadioGroup>
                </Card>
              </Collapse>

              <ListItem
                button
                onClick={() => {
                  this.setState({
                    ...this.state,
                    anchorEl: null,
                    heatmapLimits: {
                      ...this.state.heatmapLimits,
                      heatmapLimitDialog: true,
                    },
                  });
                }}
                className={classes.listItem}
              >
                <ListItemText primary="Change Heatmap Limits" />
              </ListItem>
            </Menu>
          )}
          {this.props.leftView && this.state.heatmapLimits.heatmapLimitDialog && (
            <Dialog
              open={this.state.heatmapLimits.heatmapLimitDialog}
              PaperProps={{ className: classNames(classes.dialogRoot) }}
            >
              <Typography
                className={classes.dialogTitle}
                variant="h4"
                color="textPrimary"
                align="center"
              >
                Change Heatmap Limits
              </Typography>
              <DialogContent className={classes.dialogContentContainer}>
                <div className={classes.sliderDiv}>
                  <Typography gutterBottom>E-Mode Limit</Typography>

                  <Slider
                    value={this.state.heatmapLimits.swsLimit}
                    step={0.5}
                    className={classes.sliderContainer}
                    color={"primary"}
                    aria-labelledby="discrete-slider-always"
                    min={0}
                    max={10}
                    valueLabelFormat={(value) => `${value}m/s`}
                    marks={[
                      {
                        value: 0,
                        label: "0 m/s",
                      },
                      {
                        value: 2,
                        label: "2 m/s",
                      },
                      {
                        value: 4,
                        label: "4 m/s",
                      },
                      {
                        value: 6,
                        label: "6 m/s",
                      },
                      {
                        value: 8,
                        label: "8 m/s",
                      },
                      {
                        value: 10,
                        label: "10 m/s",
                      },
                    ]}
                    valueLabelDisplay="on"
                    onChange={(e, newValue) =>
                      this.setState({
                        ...this.state,
                        heatmapLimits: {
                          ...this.state.heatmapLimits,
                          swsLimit: newValue,
                        },
                      })
                    }
                  />
                </div>
                <div className={classes.sliderDiv}>
                  <Typography gutterBottom>T-Mode Limit</Typography>

                  <Slider
                    value={this.state.heatmapLimits.thicknessLimit}
                    step={0.5}
                    className={classes.sliderContainer}
                    color={"primary"}
                    aria-labelledby="discrete-slider-always"
                    min={0}
                    max={6}
                    valueLabelFormat={(value) => `${value}mm`}
                    marks={[
                      {
                        value: 0,
                        label: "0 mm",
                      },
                      {
                        value: 1,
                        label: "1 mm",
                      },
                      {
                        value: 2,
                        label: "2 mm",
                      },
                      {
                        value: 3,
                        label: "3 mm",
                      },
                      {
                        value: 4,
                        label: "4 mm",
                      },
                      {
                        value: 5,
                        label: "5 mm",
                      },
                      {
                        value: 6,
                        label: "6 mm",
                      },
                    ]}
                    valueLabelDisplay="on"
                    onChange={(e, newValue) =>
                      this.setState({
                        ...this.state,
                        heatmapLimits: {
                          ...this.state.heatmapLimits,
                          thicknessLimit: newValue,
                        },
                      })
                    }
                  />
                </div>
              </DialogContent>

              <DialogActions className={classes.dialogButtonContainer}>
                <Button
                  onClick={() =>
                    this.setState({
                      ...this.state,
                      heatmapLimits: {
                        ...this.state.heatmapLimits,
                        swsLimit: [
                          this.props.heatmap.swsLimit[0],
                          this.props.heatmap.swsLimit[1],
                        ],
                        thicknessLimit: [
                          this.props.heatmap.thicknessLimit[0],
                          this.props.heatmap.thicknessLimit[1],
                        ],
                        heatmapLimitDialog: false,
                      },
                    })
                  }
                  variant="outlined"
                  className={classes.dialogButtonOutlined}
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => this.handleChangeHeatmapLimit()}
                  className={classes.dialogButtonPrimary}
                  variant="contained"
                  color="primary"
                  disabled={
                    this.state.heatmapLimits.swsLimit[0] ===
                      this.state.heatmapLimits.swsLimit[1] ||
                    this.state.heatmapLimits.thicknessLimit[0] ===
                      this.state.heatmapLimits.thicknessLimit[1]
                  }
                >
                  Apply Limits
                </Button>
              </DialogActions>
            </Dialog>
          )}
        </div>

        {/* COLOR BAR FOR HEATMAP*/}
        <div className={classes.mapContainer}>
          <AnimatePresence>
            {this.props.heatmap.status && (
              <motion.div
                className={classes.colorScaleWrapper}
                initial={{ x: 60 }}
                animate={{ x: 0 }}
                exit={{ x: 60 }}
              >
                <ColorScale
                  heatmapType={this.props.heatmap}
                  value={
                    this.props.heatmap.type === heatmapEmodeType
                      ? this.props.studyLocations[this.props.selectedLocationId]
                          .shearWaveSpeed.value
                      : this.props.heatmap.type === heatmapTmodeType
                      ? this.props.studyLocations[this.props.selectedLocationId]
                          .thickness.value
                      : null
                  }
                />
              </motion.div>
            )}
          </AnimatePresence>

          {/* BODY IMAGE */}
          <img
            src={maps[this.props.currentMapId].image} // Display the base64 for the currentMapId
            className={classes.image}
            alt={"Outline of body with acquisition locations mapped onto it."}
            ref={this.imageRef}
            style={{
              minWidth:
                this.props.currentMapId === customFaceMapId
                  ? "calc(100% - 50px)" //"100%"
                  : "unset",
              // maxWidth: "calc(100% - 50px)"
              // this.props.currentMapId === "CUSTOM_FACE_MAP"
              //   ? "calc(100% - 50px)" //"100%"
              //   : "unset",
            }}
          />

          <div
            className={classes.locationsContainer}
            style={{
              height: this.state.imgHeight,
              width: this.state.imgWidth,
              zIndex: 2,
            }}
            ref={this.locationsContainer}
          >
            {(this.props.currentLocationView === "BODY_MAP" ||
              this.props.leftView) && ( // dont bother rendering bodymap location points if they are not in view
              <svg
                height={this.state.imgHeight}
                width={this.state.imgWidth}
                onMouseEnter={() => {
                  this.setState({ ...this.state, imgHover: true });
                }}
                onMouseMove={(event) => this.onDragOver(event, "CURSOR")}
                onTouchMove={(event) => this.onDragOver(event, "TOUCH")}
                onMouseLeave={(e) => {
                  let container = this.imageRef.current.getBoundingClientRect();
                  if (
                    e.clientX > container.left &&
                    e.clientX < container.right &&
                    e.clientY > container.top &&
                    e.clientY < container.bottom
                  ) {
                  } else {
                    this.setState({ ...this.state, imgHover: false });
                  }
                }}
              >
                {this.props.mapLocationIds.map((id) => {
                  // if (
                  //   !(
                  //     //the cases where the face region should NOT be rendered
                  //     (
                  //       this.props.faceMap &&
                  //       !this.state.imgHover &&
                  //       !(this.props.selectedLocationId === id) &&
                  //       ((this.props.heatmap.status &&
                  //         !this.props.locations[id].shearWaveSpeed.value) ||
                  //         !this.props.heatmap.status) &&
                  //       !this.props.editMapMode &&
                  //       !this.props.leftView
                  //     )
                  //   )
                  // ) {
                  return (
                    <MapRegion
                      imgWidth={this.state.imgWidth}
                      imgHeight={this.state.imgHeight}
                      key={id}
                      id={id}
                      dragStart={this.dragStart}
                      dragEnd={this.dragEnd}
                    />
                  );
                  // }
                })}
              </svg>
            )}
          </div>
        </div>
      </Grid>
    );
  }
}

// this uses the reselect library
// memorizes the (expensive) filter calculation and only updates it when one of its input variables change
const filterLocations = createSelector(
  [
    (state) => state.locations.allIds,
    (state) => state.locations.byId,
    (state) => state.locations.currentMapId,
    (state) => state.protocol.locations,
  ],
  (allIds, locationsById, currentMapId, protocolLocations) => {
    return allIds.filter((locationId) => {
      const location = protocolLocations[locationId];

      //Only locations that have coordinated for the chosen bodymap AND are note removed from study

      if (location.maps[currentMapId] && !location.removed) {
        return true;
      }
      return false;
    });
  }
);

const mapStateToProps = (state) => {
  let mapIds = Object.keys(state.protocol.maps);
  // If there is a custom face map, then do not display other Face Maps
  if (mapIds.includes(customFaceMapId)) {
    if (mapIds.indexOf(faceMapId) > -1) {
      mapIds.splice(mapIds.indexOf(faceMapId), 1);
    }
  }

  return {
    heatmap: state.utility.heatmap,
    currentLocationView: state.locations.currentLocationView,
    selectedLocationId: state.locations.selectedLocationId,
    mapLocationIds: filterLocations(state), // only subset of location ids that are in currently selected bodyMap
    currentMapId: state.locations.currentMapId,
    maps: state.protocol.maps,
    mapIds: mapIds,
    studyLocations: state.locations.byId,
    protocolLocations: state.protocol.locations,
    faceMap: [faceMapId, customFaceMapId].includes(
      state.locations.currentMapId
    ),
    acquireBar: state.utility.showAcquireBar,
    leftView: state.view.mainViewSection.orientation === "LEFT",
    editMapMode: state.utility.mode.editMapMode,
    disableEditMenu: !state.utility.samplesDone || state.device.acquiring,
    theme: state.utility.theme,
  };
};

export default connect(mapStateToProps)(withStyles(styles)(BodyMap));
